/**
 * @file blendSearch.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-09-17
 * @description Which materials a blend uses — decided exactly, by branch and
 *              bound over the LP the solvers already solve.
 *
 *              "Minimum 4 ingredients" is not a linear constraint. The LP can
 *              say how much of each material to use; it cannot say "use at least
 *              four of them", because being in the blend is a yes-or-no fact and
 *              a simplex only deals in amounts. Ask it for the cheapest 16%
 *              protein blend and it will use exactly as few materials as the
 *              arithmetic needs, which is usually two or three.
 *
 *              So each material gets a decision on top of the LP: IN (held at a
 *              floor — the minimum inclusion the request stated, or otherwise the
 *              smallest inclusion that shows and prints, so it genuinely counts
 *              as an ingredient rather than 0.0000001% of one) or OUT (held at
 *              zero). A stated minimum with no count is the same decision: every
 *              material at that figure or more, or not at all. Every node of
 *              the search is an ordinary LP with some materials pushed in and
 *              some taken out, and its cost is a lower bound on anything below it
 *              in the tree — which is what lets whole branches be discarded
 *              without being solved, and what makes the answer the proved
 *              cheapest rather than a plausible one.
 *
 *              The same machinery answers "three variations". A variation must
 *              leave out at least one of the main ingredients of each blend
 *              before it — a set of materials that may not all be present — and
 *              that is exactly the kind of yes-or-no rule this searches over.
 *
 *              BOUNDED, ALWAYS. The search runs on the main thread, as the solver
 *              always has, so it stops at a node limit and a deadline. Stopping
 *              with an answer returns that answer marked as not proved, with the
 *              most it could be above the true cheapest — it still keeps every
 *              rule. Stopping without one returns 'limit', never a blend that
 *              breaks the rule it could not satisfy.
 *
 *              Where the limit actually bites: many ingredients and no spec to
 *              choose between them ("at least 12 ingredients" and nothing else).
 *              Dozens of materials then tie to the penny at the floor, the
 *              first answer found is the right one, and proving it means
 *              visiting every tie. That is why a stop reports its gap rather
 *              than pretending to be a failure.
 */

/** Anything smaller than this is zero: it is what a pivot leaves behind. */
const EPS = 1e-7;

/**
 * Default limits for one search.
 *
 * Each LP here is tens of microseconds to a fraction of a millisecond — forty-odd
 * materials, a handful of rows — so the deadline is what normally stops a long
 * search, and the node count is a backstop for a machine where the clock is not
 * the constraint. Real briefs ("at least 4", "no more than 7", three variations)
 * settle in well under a hundred LPs.
 */
export const SEARCH_LIMITS = Object.freeze({ nodes: 20000, ms: 500 });

/**
 * @param {Object}   opts
 * @param {string[]} opts.keys        materials the search may decide on
 * @param {Function} opts.solve       ({forcedIn:Set, forcedOut:Set}) =>
 *                                    {status, x:{key:value}, cost}. forcedIn is
 *                                    held at its floor or above, forcedOut at 0.
 * @param {string[]} [opts.mandatory] keys already kept in by their own lower
 *                                    bound (a named material, "minimum 2%
 *                                    molasses") — counted, never branched out
 * @param {number}   [opts.fixedCount] ingredients outside `keys` that are in
 *                                    regardless (locked materials)
 * @param {number}   [opts.minCount]
 * @param {number}   [opts.maxCount]
 * @param {Function} [opts.floorOf]   key => the smallest inclusion that counts as
 *                                    using it. 0 turns the floor off.
 * @param {string[][]} [opts.cuts]    each a set of keys at least one of which
 *                                    must be absent
 * @param {number}   [opts.nodeLimit] most LPs to solve
 * @param {number}   [opts.deadline]  absolute Date.now() value
 * @param {number}   [opts.gap]       prune a node unless it can beat the best
 *                                    found by more than this — a penny a tonne
 *
 * @returns {{status:'optimal'|'infeasible'|'limit'|string,
 *            best:{x, cost, support:string[]}|null,
 *            nodes:number, proved:boolean, lowerBound:number|null}}
 *          lowerBound is the least any blend keeping the rules could score, so
 *          best.cost - lowerBound is the most an unproved answer can be off by.
 */
export function searchSupport({
  keys,
  solve,
  mandatory = [],
  fixedCount = 0,
  minCount = null,
  maxCount = null,
  floorOf = () => 0,
  cuts = [],
  nodeLimit = SEARCH_LIMITS.nodes,
  deadline = null,
  gap = 0.01
}) {
  const mandatorySet = new Set(mandatory);
  const cutSets = (cuts || []).filter((c) => c && c.length).map((c) => [...new Set(c)]);
  const hasMin = Number.isFinite(minCount) && minCount > 0;
  const hasMax = Number.isFinite(maxCount) && maxCount > 0;

  let nodes = 0;
  let limited = false;
  let best = null;
  // The bound of a node the deadline interrupted part-way through its own work.
  let interruptedBound = Infinity;
  // A material that cannot be forced in even at the root can never be in: every
  // other node is the root with more constraints added.
  const neverIn = new Set();
  // How much forcing each material in costs, measured once where it is first
  // needed. Only an ORDERING — the cheapest addition is tried first so a good
  // answer turns up early and prunes the rest — never a bound.
  const penalty = new Map();
  let rootFailure = null;

  const overBudget = () => nodes >= nodeLimit || (deadline !== null && Date.now() > deadline);

  const runLP = (forcedIn, forcedOut) => {
    nodes += 1;
    return solve({ forcedIn, forcedOut });
  };

  const with_ = (set, key) => { const next = new Set(set); next.add(key); return next; };

  const keepsRules = (x, forcedIn) => {
    const used = keys.filter((k) => (x[k] || 0) > EPS);
    const usedSet = new Set(used);
    const count = fixedCount + used.length;
    return (!hasMin || count >= minCount)
      && (!hasMax || count <= maxCount)
      && used.every((k) => mandatorySet.has(k) || forcedIn.has(k) || x[k] >= floorOf(k) - EPS)
      && !cutSets.some((cut) => cut.every((k) => usedSet.has(k)));
  };

  // Every node carries the cost of the parent it came from: nothing below a node
  // can be cheaper than its parent, which is what the lower bound is built from.
  const stack = [{ inSet: new Set(), outSet: new Set(), reuse: null, bound: -Infinity }];

  while (stack.length) {
    if (overBudget()) { limited = true; break; }
    const node = stack.pop();

    const inAll = new Set([...node.inSet, ...mandatorySet]);
    const outAll = new Set([...node.outSet, ...neverIn]);
    const isRoot = node.inSet.size === 0 && node.outSet.size === 0;

    // ── Pruned without solving anything ─────────────────────────────────────
    if (hasMax && fixedCount + inAll.size > maxCount) continue;
    if (hasMin && fixedCount + keys.filter((k) => !outAll.has(k)).length < minCount) continue;
    if (cutSets.some((cut) => cut.every((k) => inAll.has(k)))) continue;
    if ([...inAll].some((k) => outAll.has(k))) continue;
    if (best && node.bound >= best.cost - gap) continue;

    const r = node.reuse || runLP(node.inSet, outAll);
    if (r.status !== 'optimal') {
      if (isRoot && r.status !== 'infeasible') rootFailure = r.status;
      continue;
    }
    // Nothing below this node can be cheaper than this node.
    if (best && r.cost >= best.cost - gap) continue;

    const x = r.x || {};
    const used = keys.filter((k) => (x[k] || 0) > EPS);
    const count = fixedCount + used.length;

    let branchKey = null;
    let inFirst = false;

    // 1. A material in at a trace. It is either properly in or properly out.
    const thin = used
      .filter((k) => !node.inSet.has(k) && !mandatorySet.has(k) && x[k] < floorOf(k) - EPS)
      .sort((a, b) => x[b] - x[a]);
    if (thin.length) {
      branchKey = thin[0];
      inFirst = hasMin && count <= minCount;
    }

    // 2. A variation that still contains every main ingredient of an earlier one.
    if (!branchKey) {
      const usedSet = new Set(used);
      const violated = cutSets.find((cut) => cut.every((k) => usedSet.has(k)));
      if (violated) {
        const removable = violated.filter((k) => !inAll.has(k)).sort((a, b) => x[a] - x[b]);
        if (!removable.length) continue;
        branchKey = removable[0];
        inFirst = false;
      }
    }

    // 3. Too many ingredients: take out the least of them first.
    if (!branchKey && hasMax && count > maxCount) {
      const removable = used.filter((k) => !inAll.has(k)).sort((a, b) => x[a] - x[b]);
      if (!removable.length) continue;
      branchKey = removable[0];
      inFirst = false;
    }

    // 4. Too few: bring in whatever costs least to add.
    if (!branchKey && hasMin && count < minCount) {
      // Not already decided: a material forced in but sitting at zero (a floor
      // of nothing) would otherwise be chosen again, forever.
      const addable = keys.filter((k) => !((x[k] || 0) > EPS) && !outAll.has(k) && !inAll.has(k));
      if (!addable.length) continue;

      for (const k of addable) {
        if (penalty.has(k)) continue;
        if (overBudget()) { limited = true; break; }
        const forced = with_(node.inSet, k);
        const probe = runLP(forced, outAll);
        if (probe.status !== 'optimal') {
          penalty.set(k, Infinity);
          if (isRoot && probe.status === 'infeasible') neverIn.add(k);
          continue;
        }
        penalty.set(k, probe.cost - r.cost);
        // A probe is a whole candidate blend. If it already keeps every rule it
        // is an answer, and finding one this early is what prunes the rest.
        if (keepsRules(probe.x, forced) && (!best || probe.cost < best.cost - 1e-9)) {
          best = { x: probe.x, cost: probe.cost, support: keys.filter((key) => (probe.x[key] || 0) > EPS) };
        }
      }
      if (limited) { interruptedBound = Math.min(interruptedBound, r.cost); break; }

      const ranked = addable
        .filter((k) => !neverIn.has(k) && penalty.get(k) !== Infinity)
        .sort((a, b) => (penalty.get(a) ?? 0) - (penalty.get(b) ?? 0));
      if (!ranked.length) continue;
      branchKey = ranked[0];
      inFirst = true;
    }

    // Keeps every rule.
    if (!branchKey) {
      if (!best || r.cost < best.cost - 1e-9) best = { x, cost: r.cost, support: used };
      continue;
    }

    const value = x[branchKey] || 0;
    const inChild = {
      inSet: with_(node.inSet, branchKey),
      outSet: node.outSet,
      // Already at or above its floor: forcing it in changes nothing, so the
      // parent's answer stands and need not be solved again.
      reuse: value > EPS && value >= floorOf(branchKey) - EPS ? r : null,
      bound: r.cost
    };
    const outChild = {
      inSet: node.inSet,
      outSet: with_(node.outSet, branchKey),
      // Not used at all: taking it away changes nothing either.
      reuse: value <= EPS ? r : null,
      bound: r.cost
    };
    // Last pushed is explored first.
    if (inFirst) stack.push(outChild, inChild);
    else stack.push(inChild, outChild);
  }

  // Everything not yet explored is on the stack (or was interrupted mid-node),
  // and none of it can come in under its parent's cost.
  const lowerBound = (() => {
    if (!limited) return best ? best.cost : null;
    const open = Math.min(interruptedBound, ...stack.map((n) => n.bound));
    const floor = best ? Math.min(best.cost, open) : open;
    return Number.isFinite(floor) ? floor : null;
  })();

  if (best) return { status: 'optimal', best, nodes, proved: !limited, lowerBound };
  if (limited) return { status: 'limit', best: null, nodes, proved: false, lowerBound };
  if (rootFailure) return { status: rootFailure, best: null, nodes, proved: true, lowerBound: null };
  return { status: 'infeasible', best: null, nodes, proved: true, lowerBound: null };
}

export default searchSupport;
